// Public /book iframe page — no auth, no chrome. Embeds cleanly in Shopify.
// Flow: pickup + return → available cars → client details → submit → thank-you.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api/public` });

const inputCls = "w-full h-11 px-3 rounded-md border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900 transition-colors";
const labelCls = "block text-xs uppercase tracking-wider text-neutral-500 mb-1.5 font-medium";
const btnPrimary = "inline-flex items-center justify-center h-14 px-10 rounded-md bg-white text-neutral-900 font-semibold text-base border-2 border-neutral-900 hover:bg-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:border-neutral-300 disabled:cursor-not-allowed transition-colors";
const btnGhost = "inline-flex items-center justify-center h-11 px-4 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100 transition-colors";

function BubbleGroup({ label, value, onChange, testid }) {
  const set = (v) => onChange(value === v ? null : v);
  return (
    <div>
      <div className={labelCls}>{label}</div>
      <div className="flex gap-2" data-testid={testid}>
        {["yes", "no"].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => set(opt)}
            data-testid={`${testid}-${opt}`}
            className={`h-11 px-6 rounded-md border font-medium transition-colors ${
              (value === opt) ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"
            }`}
          >
            {opt === "yes" ? "Yes" : "No"}
          </button>
        ))}
      </div>
    </div>
  );
}

function VehicleCard({ v, selected, onClick }) {
  const busy = v.available === false;
  return (
    <button
      type="button"
      onClick={() => !busy && onClick(v)}
      disabled={busy}
      data-testid={`public-vehicle-${v.id}`}
      className={`text-left rounded-xl border transition-all ${
        selected ? "border-neutral-900 ring-2 ring-neutral-900/10 shadow-md"
                 : busy ? "border-neutral-200 opacity-50 cursor-not-allowed"
                        : "border-neutral-200 hover:border-neutral-500 hover:shadow-md"
      } bg-white overflow-hidden`}
    >
      <div className="aspect-[16/10] bg-neutral-100 overflow-hidden">
        {v.image_url ? (
          <img src={v.image_url.startsWith("/api/") ? `${BACKEND}${v.image_url}` : v.image_url} alt={v.website_name || `${v.make} ${v.model}`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">No photo</div>
        )}
      </div>
      <div className="p-4">
        <div className="font-semibold text-neutral-900">{v.website_name || `${v.make} ${v.model}`}</div>
        <div className="text-xs text-neutral-500 mt-1">{v.year || ""} {v.color ? `· ${v.color}` : ""} {v.seats ? `· ${v.seats} seats` : ""}</div>
        {busy && <div className="mt-2 inline-block text-[10px] uppercase tracking-wider bg-red-50 text-red-600 px-2 py-0.5 rounded">Booked for these dates</div>}
        {!busy && selected && <div className="mt-2 inline-block text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Selected</div>}
      </div>
    </button>
  );
}

export default function PublicBook() {
  const [step, setStep] = useState(1); // 1=dates, 2=car, 3=details, 4=thanks
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [details, setDetails] = useState({
    customer_first_name: "", customer_last_name: "",
    customer_phone: "", customer_email: "",
    client_age: "",
    has_license_class6: null,
    has_insurance: null, has_rental_coverage: null, insurance_company: "",
    has_cc_rental_coverage: null,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Fetch catalog whenever dates change (also on first load — no dates → shows availability=true).
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = start && end ? { start, end } : {};
        const { data } = await api.get("/rental-catalog", { params });
        if (!cancel) setVehicles(data);
      } catch { if (!cancel) setVehicles([]); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [start, end]);

  const availableCount = useMemo(() => vehicles.filter((v) => v.available !== false).length, [vehicles]);

  const canGoDetails = !!picked && !!start && !!end;
  const canSubmit =
    canGoDetails &&
    details.customer_first_name.trim() &&
    details.customer_last_name.trim() &&
    details.customer_phone.trim() &&
    details.client_age.trim() &&
    details.has_license_class6 !== null &&
    details.has_insurance !== null &&
    details.has_cc_rental_coverage !== null &&
    (details.has_insurance !== "yes" || (details.has_rental_coverage !== null && details.insurance_company.trim()));

  const submit = async () => {
    setErrorMsg("");
    if (!canSubmit) { setErrorMsg("Please fill in every required field before submitting."); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        vehicle_id: picked.id,
        start_datetime: start,
        end_datetime: end,
        customer_name: `${details.customer_first_name.trim()} ${details.customer_last_name.trim()}`.trim(),
        customer_phone: details.customer_phone.trim(),
        customer_email: details.customer_email.trim(),
        client_age: details.client_age.trim(),
        has_license_class6: details.has_license_class6 === "yes",
        has_insurance: details.has_insurance === "yes",
        has_rental_coverage: details.has_insurance === "yes" ? details.has_rental_coverage === "yes" : null,
        insurance_company: details.has_insurance === "yes" ? details.insurance_company.trim() : "",
        has_cc_rental_coverage: details.has_cc_rental_coverage === "yes",
        notes: details.notes.trim(),
      });
      setStep(4);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Something went wrong. Please try again or call the shop.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="native-dates min-h-screen bg-neutral-50 text-neutral-900 py-8 px-4 sm:px-6" data-testid="public-book-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="public-book-heading">Book a Rental</h1>
          <p className="text-neutral-500 mt-2">Choose your dates, pick your car, tell us a bit about you — we&apos;ll take it from there.</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-8 text-xs uppercase tracking-wider text-neutral-500" data-testid="public-book-steps">
          {["Dates", "Car", "Details", "Done"].map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <div key={label} className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  active ? "bg-neutral-900 text-white" : done ? "bg-emerald-600 text-white" : "bg-neutral-200 text-neutral-500"
                }`}>{done ? "✓" : n}</span>
                <span className={active ? "text-neutral-900 font-medium" : ""}>{label}</span>
                {i < 3 && <span className="text-neutral-300">—</span>}
              </div>
            );
          })}
        </div>

        {/* Step 1 — dates */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="public-book-step-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Pickup date &amp; time</label>
                <input
                  type="datetime-local"
                  min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  step="300"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className={inputCls}
                  data-testid="public-book-start"
                />
              </div>
              <div>
                <label className={labelCls}>Return date &amp; time</label>
                <input
                  type="datetime-local"
                  min={start || new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  step="300"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  className={inputCls}
                  data-testid="public-book-end"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!start || !end || end <= start}
                className={btnPrimary}
                data-testid="public-book-next-1"
              >Next → choose a car</button>
            </div>
          </div>
        )}

        {/* Step 2 — cars */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="public-book-step-2">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="text-sm text-neutral-500">
                {loading ? "Loading fleet…" : `${availableCount} of ${vehicles.length} cars available for your dates.`}
              </div>
              <button type="button" onClick={() => setStep(1)} className={btnGhost} data-testid="public-book-back-2">← Change dates</button>
            </div>
            {vehicles.length === 0 && !loading && (
              <div className="p-10 text-center text-neutral-500">No vehicles are available on our public catalogue right now.</div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((v) => (
                <VehicleCard key={v.id} v={v} selected={picked?.id === v.id} onClick={setPicked} />
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={!picked}
                className={btnPrimary}
                data-testid="public-book-next-2"
              >Next → your info</button>
            </div>
          </div>
        )}

        {/* Step 3 — details */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="public-book-step-3">
            <div className="mb-4 text-sm text-neutral-500">
              Requesting <b className="text-neutral-900">{picked.website_name || `${picked.make} ${picked.model}`}</b>
              {" · "}
              {new Date(start).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              {" → "}
              {new Date(end).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>First name</label>
                <input className={inputCls} value={details.customer_first_name} onChange={(e) => setDetails({ ...details, customer_first_name: e.target.value })} data-testid="public-book-first-name" />
              </div>
              <div>
                <label className={labelCls}>Last name</label>
                <input className={inputCls} value={details.customer_last_name} onChange={(e) => setDetails({ ...details, customer_last_name: e.target.value })} data-testid="public-book-last-name" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input className={inputCls} value={details.customer_phone} onChange={(e) => setDetails({ ...details, customer_phone: e.target.value })} data-testid="public-book-phone" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" className={inputCls} value={details.customer_email} onChange={(e) => setDetails({ ...details, customer_email: e.target.value })} data-testid="public-book-email" />
              </div>
              <div>
                <label className={labelCls}>Age</label>
                <input type="number" className={inputCls} value={details.client_age} onChange={(e) => setDetails({ ...details, client_age: e.target.value })} data-testid="public-book-age" />
              </div>
              <div className="sm:col-span-1">
                <BubbleGroup label="G / Class 6 licence?" value={details.has_license_class6} onChange={(v) => setDetails({ ...details, has_license_class6: v })} testid="public-book-license" />
              </div>
              <div className="sm:col-span-2">
                <BubbleGroup label="Do you have insurance?" value={details.has_insurance} onChange={(v) => setDetails({ ...details, has_insurance: v, ...(v !== "yes" ? { has_rental_coverage: null, insurance_company: "" } : {}) })} testid="public-book-insurance" />
              </div>
              {details.has_insurance === "yes" && (
                <>
                  <div>
                    <BubbleGroup label="Does your policy include rental coverage?" value={details.has_rental_coverage} onChange={(v) => setDetails({ ...details, has_rental_coverage: v })} testid="public-book-rental-cov" />
                  </div>
                  <div>
                    <label className={labelCls}>Insurance company</label>
                    <input className={inputCls} value={details.insurance_company} onChange={(e) => setDetails({ ...details, insurance_company: e.target.value })} placeholder="e.g. Intact, Aviva, TD…" data-testid="public-book-insurance-co" />
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <BubbleGroup label="Credit card with rental coverage?" value={details.has_cc_rental_coverage} onChange={(v) => setDetails({ ...details, has_cc_rental_coverage: v })} testid="public-book-cc-cov" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Anything else we should know? (optional)</label>
                <textarea rows={3} className={`${inputCls} h-auto py-2`} value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} data-testid="public-book-notes" />
              </div>
            </div>
            {errorMsg && <div className="mt-4 text-sm text-red-600" data-testid="public-book-error">{errorMsg}</div>}
            <div className="mt-6 flex justify-between">
              <button type="button" onClick={() => setStep(2)} className={btnGhost} data-testid="public-book-back-3">← Back</button>
              <button type="button" onClick={submit} disabled={!canSubmit || submitting} className={btnPrimary} data-testid="public-book-submit">
                {submitting ? "Sending…" : "Request booking"}
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — thank-you */}
        {step === 4 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center" data-testid="public-book-step-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <span className="text-emerald-700 text-2xl">✓</span>
            </div>
            <h2 className="text-2xl font-semibold mb-2">Booking request received</h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Thanks — we&apos;ve got it. You&apos;ll receive a confirmation for your booking between
              <b> 10 AM and noon the following day</b>, or the <b>same day</b> if you booked before 10 AM.
              A team member may reach out to verify your info before we lock in the reservation.
            </p>
            <p className="text-xs text-neutral-500 mt-6">Reference: request submitted from our public booking form. Feel free to close this window.</p>
          </div>
        )}

        <div className="text-center text-[11px] text-neutral-400 mt-8">Powered by 01 Auto Group</div>
      </div>
    </div>
  );
}
